import {
    assertPortableGovernedModelRouteV1,
    assertPortableGovernedRoutingReceiptV1,
    assertPortableGovernedSupportRoutingReceiptV1,
    resolvePortableGovernedSupportRouteV1,
    type PortableGovernedModelRouteV1,
    type PortableGovernedRoutingReceiptV1,
    type PortableGovernedSupportRouteV1,
    type PortableGovernedSupportRoutingReceiptV1,
    type QuoteEngineGovernedSupportOperationV1,
} from './model-routing';
import { canonicalJson } from './canonical-json';

interface GovernedRouteContextV1 {
    route: PortableGovernedModelRouteV1;
    supportRoutes: Map<QuoteEngineGovernedSupportOperationV1, PortableGovernedSupportRouteV1>;
    receiptRefs: Set<string>;
    active: boolean;
}

const routeBySignal = new WeakMap<AbortSignal, GovernedRouteContextV1>();
const SUPPORT_RECEIPT_REF_PREFIX = 'governed-support-routing-receipt-v1:';

/** Decode the full privacy-safe support receipt retained inside a stage ref. */
export function decodeGovernedSupportRoutingReceiptRefV1(reference: string): unknown {
    if (typeof reference !== 'string' || !reference.startsWith(SUPPORT_RECEIPT_REF_PREFIX)) {
        throw new Error('GOVERNED_ROUTING_RECEIPT_INVALID: support receipt reference is not reconstructible');
    }
    try {
        return JSON.parse(reference.slice(SUPPORT_RECEIPT_REF_PREFIX.length));
    } catch {
        throw new Error('GOVERNED_ROUTING_RECEIPT_INVALID: support receipt envelope is malformed');
    }
}

function sameRoute(left: PortableGovernedModelRouteV1, right: PortableGovernedModelRouteV1): boolean {
    return left.routingReceiptRef === right.routingReceiptRef
        && left.configurationSnapshotHash === right.configurationSnapshotHash
        && left.runId === right.runId
        && left.stage === right.stage;
}

/**
 * Scope a governed route to the stage's existing AbortSignal. This avoids a
 * mutable global route and reaches nested legacy helpers without inventing
 * unsupported parameters on each historical service contract.
 */
export async function withPortableGovernedModelRouteV1<T>(
    route: PortableGovernedModelRouteV1,
    signal: AbortSignal | undefined,
    operation: () => Promise<T>,
): Promise<{ value: T; receiptRefs: readonly string[] }> {
    assertPortableGovernedModelRouteV1(route);
    if (!signal) throw new Error('GOVERNED_MODEL_ROUTE_INVALID: an AbortSignal is required');
    const active = routeBySignal.get(signal);
    if (active) {
        if (!active.active) {
            throw new Error('GOVERNED_MODEL_ROUTE_INVALID: stage route context is already closed');
        }
        if (!sameRoute(active.route, route)) {
            throw new Error('GOVERNED_MODEL_ROUTE_INVALID: signal is already bound to another route');
        }
        const value = await operation();
        return { value, receiptRefs: Object.freeze([...active.receiptRefs]) };
    }

    const context: GovernedRouteContextV1 = {
        route,
        supportRoutes: new Map(),
        receiptRefs: new Set<string>(),
        active: true,
    };
    routeBySignal.set(signal, context);
    try {
        const value = await operation();
        return { value, receiptRefs: Object.freeze([...context.receiptRefs]) };
    } finally {
        // Keep a weak tombstone for this isolated stage signal. Any detached
        // legacy task that outlives its stage must fail closed instead of
        // re-entering the ordinary, ungoverned gateway path.
        context.active = false;
    }
}

/** Read-only lookup used at the concrete browser AI gateway boundary. */
export function governedModelRouteForSignalV1(
    signal: AbortSignal | undefined,
): PortableGovernedModelRouteV1 | null {
    if (!signal) return null;
    const context = routeBySignal.get(signal);
    if (!context) return null;
    if (!context.active) {
        throw new Error('GOVERNED_MODEL_ROUTE_INVALID: stage route context is already closed');
    }
    assertPortableGovernedModelRouteV1(context.route);
    return context.route;
}

/**
 * Resolve the support-role assignment from the same immutable run snapshot as
 * the active stage. Mutable model selectors never participate in this path.
 */
export function governedSupportRouteForSignalV1(
    signal: AbortSignal | undefined,
    operation: QuoteEngineGovernedSupportOperationV1,
): PortableGovernedSupportRouteV1 | null {
    if (!signal) return null;
    const context = routeBySignal.get(signal);
    if (!context) return null;
    if (!context.active) {
        throw new Error('GOVERNED_MODEL_ROUTE_INVALID: stage route context is already closed');
    }
    assertPortableGovernedModelRouteV1(context.route);
    const cached = context.supportRoutes.get(operation);
    if (cached) return cached;
    const supportRoute = resolvePortableGovernedSupportRouteV1(context.route, operation);
    context.supportRoutes.set(operation, supportRoute);
    return supportRoute;
}

/** Bind one backend-produced privacy-safe routing proof to the active stage. */
export function recordGovernedRoutingReceiptV1(
    signal: AbortSignal | undefined,
    receipt: PortableGovernedRoutingReceiptV1 | PortableGovernedSupportRoutingReceiptV1,
): string {
    if (!signal) throw new Error('GOVERNED_ROUTING_RECEIPT_INVALID: an AbortSignal is required');
    const context = routeBySignal.get(signal);
    if (!context) throw new Error('GOVERNED_ROUTING_RECEIPT_INVALID: no governed stage owns this signal');
    if (!context.active) throw new Error('GOVERNED_ROUTING_RECEIPT_INVALID: governed stage is already closed');
    let receiptRef: string;
    if (receipt.schemaVersion === 'quote-engine-governed-support-routing-receipt-v1') {
        const supportRoute = governedSupportRouteForSignalV1(signal, receipt.operation);
        if (!supportRoute) {
            throw new Error('GOVERNED_ROUTING_RECEIPT_INVALID: support route is not bound');
        }
        assertPortableGovernedSupportRoutingReceiptV1(receipt, supportRoute);
        // StageResultV1 deliberately stores string refs. Inline the complete
        // privacy-safe envelope so the stage receipt durably hashes something
        // a later verifier can reconstruct, instead of an opaque digest only.
        receiptRef = `${SUPPORT_RECEIPT_REF_PREFIX}${canonicalJson(receipt as any)}`;
    } else {
        assertPortableGovernedRoutingReceiptV1(receipt, context.route);
        receiptRef = `governed-routing-receipt:${receipt.receiptHash}`;
    }
    context.receiptRefs.add(receiptRef);
    return receiptRef;
}
